import { type ReactNode } from 'react';
import type { Table } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, Columns3, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';

/** Bordered card holding a view's filter controls. One per table, always. */
export function FilterBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mb-3 flex flex-col gap-2.5 rounded-lg border border-border bg-background p-3',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Row inside a FilterBar. */
export function FilterRow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-wrap items-center gap-2', className)}>{children}</div>;
}

/** Label + control pairing for the numeric/date range inputs. */
export function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

export function ResetFiltersButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" onClick={onClick}>
      <X className="h-3.5 w-3.5" />
      Reset
    </Button>
  );
}

/**
 * Show/hide columns. Answers the review's "reduce clutter when not all data is
 * needed at once" — columns stay in the table, the operator picks what shows.
 */
export function ColumnsMenu<TData>({ table, hidden = [] }: { table: Table<TData>; hidden?: string[] }) {
  const toggleable = table
    .getAllLeafColumns()
    .filter(c => c.getCanHide() && !hidden.includes(c.id));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <Columns3 className="h-3.5 w-3.5" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[210px]">
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        {toggleable.map(column => {
          const meta = column.columnDef.meta as { label?: string } | undefined;
          return (
            <DropdownMenuCheckboxItem
              key={column.id}
              checked={column.getIsVisible()}
              onCheckedChange={value => column.toggleVisibility(!!value)}
            >
              {meta?.label ?? column.id}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Bulk-action bar shown once rows are selected.
 *
 * Labels are "Accept"/"Skip", not "Accept all"/"Skip all" — the old wording
 * implied the whole result set when the action only ever touched the ticked
 * rows (BOT-392). The count sits right next to them to remove any doubt.
 */
export function BulkActionBar({
  count,
  total,
  onClear,
  children,
}: {
  count: number;
  total?: number;
  onClear: () => void;
  children: ReactNode;
}) {
  return (
    <div className="mb-3 flex h-11 items-center gap-2.5 rounded-lg border border-border bg-zinc-50 px-3">
      <span className="text-[13px] font-medium">
        {count} {count === 1 ? 'row' : 'rows'} selected
        {total != null && <span className="text-muted-foreground"> of {total.toLocaleString('en-US')}</span>}
      </span>
      <Separator orientation="vertical" />
      {children}
      <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
        Clear selection
      </Button>
    </div>
  );
}

/** Footer: selection summary on the left, page size + pager on the right. */
export function TablePagination({
  page,
  totalPages,
  total,
  perPage,
  pageSizes,
  onPageChange,
  onPerPageChange,
  selectedCount = 0,
}: {
  page: number;
  totalPages: number;
  total: number;
  perPage: number;
  pageSizes: readonly number[];
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  selectedCount?: number;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-4 px-0.5 py-3">
      <span className="text-[12.5px] text-muted-foreground">
        {selectedCount > 0
          ? `${selectedCount} of ${total.toLocaleString('en-US')} rows selected`
          : `${total.toLocaleString('en-US')} ${total === 1 ? 'row' : 'rows'}`}
      </span>

      <div className="ml-auto flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] text-muted-foreground">Rows per page</span>
          <Select value={String(perPage)} onValueChange={v => onPerPageChange(Number(v))}>
            <SelectTrigger className="w-[76px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizes.map(n => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <span className="text-[12.5px] text-muted-foreground">
          Page {page} of {Math.max(totalPages, 1)}
        </span>

        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon-lg"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="icon-lg"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Page heading: title + row count, matching the design's H1 block. */
export function PageHeading({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-baseline gap-2.5">
      <h1 className="text-xl font-semibold tracking-[-0.02em]">{title}</h1>
      {count != null && (
        <span className="text-[13px] text-muted-foreground">
          {count.toLocaleString('en-US')} {count === 1 ? 'item' : 'items'}
        </span>
      )}
      {children}
    </div>
  );
}
